import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/components/ConfirmDialog';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { getStripe, buildStripeAppearance } from '@/lib/stripeElements';
import { KpiCard as StatCard } from '@/components/KpiCard';
import { RegionalMapView } from '@/components/RegionalMapView';
import { TrendRangeTabs, getTrendRangeLabel } from '@/components/TrendRangeTabs';
import { AnalyticsTabBar } from '@/components/AnalyticsTabBar';
import { PageBanner } from '@/components/PageBanner';
import { PageHeader } from '@/components/PageHeader';
import { PageSkeleton } from '@/components/PageSkeleton';
import { LimitMeter } from '@/components/LimitMeter';
import { DataTablePagination, DataTableSortHeader, DataTableToolbar } from '@/components/DataTableControls';
import { UserAvatar } from '@/components/UserAvatar';
import { formatCellValue } from '@/lib/utils';
import {
  chartTooltipStyle,
  commonCartesianGridProps,
  commonLegendProps,
  commonXAxisProps,
  commonYAxisProps,
} from '@/lib/chartTheme';
import {
  Zap,
  TrendingUp,
  AlertTriangle,
  Calendar,
  BarChart3,
  RefreshCw,
  ExternalLink,
  Clock,
  Cpu,
  Users,
  ShoppingCart,
  MessageSquare,
  Tags,
  FileText,
  CheckCircle,
  BookOpen,
  FileQuestion,
  Sparkles,
  Crown,
  Settings,
  Loader2,
  CreditCard,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Legend,
  PieChart,
  Pie,
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
// Keyed by the values TrendRangeTabs emits (Daily/Weekly/Monthly). The tabs
// select bucket granularity over the fetched 30-day history window; the keys
// must match '24h'/'7d'/'30d' or buildTrendData falls through to the default
// and Daily silently renders identically to Monthly.
const TREND_RANGE_CONFIG = {
  '24h': { days: 30, bucket: 'day' },
  '7d': { days: 30, bucket: 'week' },
  '30d': { days: 30, bucket: 'month' },
};
const MAP_WINDOW_DAYS = { '24h': 1, '7d': 7, '30d': 30 };
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const RECENT_REQUESTS_PAGE_SIZE = 10;
const EVA_PEOPLE_TOKEN_LABEL = 'Eva People Credits';
const EVA_INSIGHTS_TOKEN_LABEL = 'Eva Insights Credits';

function formatTokens(tokens) {
  if (!tokens) return '0';
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return tokens.toString();
}

function formatPercent(value, digits = 1) {
  const numeric = Number(value || 0);
  return `${numeric.toFixed(digits)}%`;
}

function formatLatencySeconds(value) {
  if (value === null || value === undefined) return formatCellValue(value);
  return `${(Number(value || 0) / 1000).toFixed(1)} s`;
}

function formatOperationName(value, label) {
  if (label) return label;
  if (!value) return formatCellValue(value);
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getEvaModelName(model, provider, label) {
  if (label) return label;
  const normalized = String(model || '').toLowerCase();
  const providerName = String(provider || '').toLowerCase();
  if (!normalized && !providerName) return 'Eva System';
  if (normalized.includes('embed') || normalized.includes('embedding')) return 'Eva Embed';
  if (normalized.includes('opus')) return 'Eva Deep';
  if (normalized.includes('sonnet')) return 'Eva Smart';
  if (normalized.includes('haiku')) return 'Eva Fast';
  if (providerName.includes('anthropic') || providerName.includes('openai')) return 'Eva System';
  return 'Eva System';
}

const OPERATION_ICON_MAP = {
  chat: MessageSquare,
  metadata_generation: Tags,
  document_analysis: FileText,
  session_summarization: CheckCircle,
  content_generation: FileText,
  research_document: BookOpen,
  pdf_agent: FileQuestion,
};

const MODEL_ICON_MAP = {
  'Eva Smart': Sparkles,
  'Eva Fast': Zap,
  'Eva Deep': Crown,
  'Eva Embed': Tags,
  'Eva System': Settings,
};

function IconLabel({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-2">
      {Icon ? <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" /> : null}
      <span>{children}</span>
    </span>
  );
}

function parseLocalDate(date) {
  return new Date(`${date}T00:00:00`);
}

function formatMonthDay(date) {
  return date.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });
}

function getWeekStart(date) {
  const weekStart = new Date(date);
  const day = weekStart.getDay();
  const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
  weekStart.setDate(diff);
  weekStart.setHours(0, 0, 0, 0);
  return weekStart;
}

function getTrendBucket(dateString, bucket) {
  const date = parseLocalDate(dateString);

  if (bucket === 'week') {
    const weekStart = getWeekStart(date);
    return {
      key: weekStart.toISOString().slice(0, 10),
      date: `Week of ${formatMonthDay(weekStart)}`,
      sortDate: weekStart,
      fullLabel: `Week of ${weekStart.toLocaleDateString()}`,
    };
  }

  if (bucket === 'month') {
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    return {
      key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      date: monthStart.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
      sortDate: monthStart,
      fullLabel: monthStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    };
  }

  return {
    key: dateString,
    date: formatMonthDay(date),
    sortDate: date,
    fullLabel: date.toLocaleDateString(),
  };
}

export function buildTrendData(history, trendWindow) {
  const sourceRows = history?.daily_aggregates || [];
  const config = TREND_RANGE_CONFIG[trendWindow] || TREND_RANGE_CONFIG['30d'];
  const latestDate = sourceRows.reduce((latest, row) => {
    if (!row.date) return latest;
    const date = parseLocalDate(row.date);
    return !latest || date > latest ? date : latest;
  }, null);

  if (!latestDate) return [];

  const minDate = new Date(latestDate);
  minDate.setDate(latestDate.getDate() - config.days + 1);
  minDate.setHours(0, 0, 0, 0);

  const buckets = {};
  sourceRows.forEach((row) => {
    if (!row.date) return;
    const rowDate = parseLocalDate(row.date);
    if (rowDate < minDate) return;

    const bucket = getTrendBucket(row.date, config.bucket);
    if (!buckets[bucket.key]) {
      buckets[bucket.key] = {
        date: bucket.date,
        fullLabel: bucket.fullLabel,
        sortDate: bucket.sortDate,
        tokens: 0,
        requests: 0,
      };
    }
    buckets[bucket.key].tokens += Number(row.total_tokens || 0);
    buckets[bucket.key].requests += Number(row.request_count || 0);
  });

  return Object.values(buckets)
    .sort((a, b) => a.sortDate - b.sortDate)
    .map(({ sortDate: _sortDate, ...row }) => row);
}

function getCapacityProgressClass(capacity) {
  if (capacity?.limit_exceeded) return '[&>div]:bg-destructive';
  if (Number(capacity?.percent_used || 0) >= 80) return '[&>div]:bg-warning';
  return '';
}

function getUserDisplayName(row) {
  const name = (row.user_name || '').trim();
  return name || row.user_email || 'Unknown user';
}

function UserNameWithRole({ name, isSuperadmin }) {
  const showSuperadminBadge = Boolean(isSuperadmin);

  return (
    <div className="flex min-w-0 items-center gap-2">
      <p className="truncate font-medium text-foreground">{name}</p>
      {showSuperadminBadge && (
        <Badge variant="outline" className="shrink-0 border-border bg-secondary/50 px-1.5 py-0 text-[10px] font-medium text-muted-foreground">
          Superadmin
        </Badge>
      )}
    </div>
  );
}

export default function TokenUsagePage() {
  const { user, hasRole } = useAuth();
  // EIP-227: AI Usage shows org-wide credit/usage data. Approver (a content-
  // approval role) does not need it and was removed from access; analyst (an
  // analytics role) and admin keep it.
  const allowedRoles = ['admin', 'analyst'];
  const canViewTokenUsage = hasRole(allowedRoles)
    || user?.roles?.some(role => allowedRoles.includes(role));
  // The Usage-by-User table on this page stays admin-only (EIP-227 kept analyst
  // to meters only). The /usage/by-user endpoint itself allows admin/approver/
  // analyst since EIP-233 (Adoption + Overview tables). Non-admins skip the call
  // here and don't see the table.
  const isAdmin = hasRole(['admin']) || user?.roles?.includes('admin');
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState(null);
  const [mapHistory, setMapHistory] = useState(null);
  const [userBreakdown, setUserBreakdown] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [trendWindow, setTrendWindow] = useState('30d');
  const [mapWindow, setMapWindow] = useState('30d');
  const [mapLoading, setMapLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [capacityFilter, setCapacityFilter] = useState('all');
  const [sortKey, setSortKey] = useState('total_tokens');
  const [sortDirection, setSortDirection] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [recentRequestsPage, setRecentRequestsPage] = useState(1);
  const [recentSearch, setRecentSearch] = useState('');
  const [recentOperationFilter, setRecentOperationFilter] = useState('all');
  const [recentModelFilter, setRecentModelFilter] = useState('all');
  const [recentSortKey, setRecentSortKey] = useState('created_at');
  const [recentSortDirection, setRecentSortDirection] = useState('desc');
  const [topupOptions, setTopupOptions] = useState([]);
  const [_buyingTopup, setBuyingTopup] = useState(null);
  const [resettingEmail, setResettingEmail] = useState(null);
  const [ConfirmEl, confirm] = useConfirm();

  // Embedded Stripe Elements Payment Element state for token top-ups -- replaces the
  // old window.location.href redirect to Stripe-hosted Checkout. Mirrors the pattern
  // used by BillingPage.js's subscription checkout.
  const [topupClientSecret, setTopupClientSecret] = useState(null);
  const [topupPaymentIntentId, setTopupPaymentIntentId] = useState(null);
  const [topupAmount, setTopupAmount] = useState(null);
  const [topupSubmitting, setTopupSubmitting] = useState(false);
  const [topupError, setTopupError] = useState('');
  const topupElementsRef = useRef(null);
  const topupContainerRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usageData, historyData, alertsData, usersData, topupsData] = await Promise.all([
        api.get('/usage'),
        api.get('/usage/history?days=30'),
        api.get('/usage/alerts'),
        isAdmin
          ? api.get('/usage/by-user?days=30').catch(() => ({ data: { by_user: [] } }))
          : Promise.resolve({ data: { by_user: [] } }),
        api.get('/billing/stripe/token-topups').catch(() => ({ data: { packs: [] } })),
      ]);
      setUsage(usageData.data);
      setHistory(historyData.data);
      setRecentRequestsPage(1);
      setAlerts(alertsData.data?.alerts || []);
      setUserBreakdown(usersData.data?.by_user || []);
      setTopupOptions(topupsData.data?.packs || []);
    } catch (err) {
      console.error('Failed to fetch usage data:', err);
      setError(err.response?.data?.detail || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!canViewTokenUsage || activeTab !== 'world') return;

    let cancelled = false;
    const fetchMapHistory = async () => {
      setMapLoading(true);
      const days = MAP_WINDOW_DAYS[mapWindow] || 30;
      const result = await api.get(`/usage/history?days=${days}`).catch((err) => {
        console.error('Failed to fetch token map data:', err);
        return { data: null };
      });
      if (cancelled) return;
      setMapHistory(result.data);
      setMapLoading(false);
    };

    fetchMapHistory();
    return () => {
      cancelled = true;
    };
  }, [activeTab, canViewTokenUsage, mapWindow]);

  const dismissAlert = async (alertId) => {
    try {
      await api.post(`/usage/alerts/${alertId}/dismiss`);
      setAlerts(alerts.filter(a => a.id !== alertId));
    } catch (err) {
      console.error('Failed to dismiss alert:', err);
    }
  };

  const refetchUserBreakdown = useCallback(async () => {
    try {
      const { data } = await api.get('/usage/by-user?days=30');
      setUserBreakdown(data?.by_user || []);
    } catch (err) {
      console.error('Failed to refresh user usage:', err);
    }
  }, []);

  const handleResetUserUsage = async (row) => {
    const name = getUserDisplayName(row);
    const ok = await confirm({
      title: 'Reset usage limit',
      description: (
        `Reset ${name}'s personal AI usage limit so they can keep working now? `
        + 'Their usage still counts toward your organization’s monthly credit '
        + 'allocation, so this does not add credits — and if the whole '
        + 'organization’s monthly limit is reached, they stay paused until it resets.'
      ),
      confirmLabel: 'Reset limit',
    });
    if (!ok) return;
    setResettingEmail(row.user_email);
    try {
      await api.post('/usage/users/reset', { user_email: row.user_email });
      toast.success(`Usage limit reset for ${name}`);
      await refetchUserBreakdown();
    } catch (err) {
      console.error('Failed to reset user usage:', err);
      toast.error(err.response?.data?.detail || 'Failed to reset usage limit');
    } finally {
      setResettingEmail(null);
    }
  };

  const buyTopup = async (pack) => {
    setBuyingTopup(pack.llm_tokens);
    // EIP-126: client-generated per-purchase token so a double-submit / retry
    // dedups server-side (Stripe idempotency key) without blocking a later repeat buy.
    const idempotencyToken = crypto.randomUUID();
    try {
      const response = await api.post('/billing/stripe/token-topups/checkout/session', {
        token_count: pack.llm_tokens,
        bucket_scope: 'any',
        idempotency_token: idempotencyToken,
      });
      if (response.data?.client_secret) {
        // Embedded Payment Element instead of a redirect to Stripe-hosted Checkout.
        setTopupPaymentIntentId(response.data.payment_intent_id);
        setTopupAmount({ amount: response.data.amount, currency: response.data.currency, tokens: pack.tokens });
        setTopupClientSecret(response.data.client_secret);
      }
    } catch (err) {
      console.error('Failed to start token top-up checkout:', err);
      setError(err.response?.data?.detail || 'Failed to start usage purchase.');
    } finally {
      setBuyingTopup(null);
    }
  };

  // Mounts the Payment Element once the top-up dialog has a client_secret.
  useEffect(() => {
    if (!topupClientSecret) return;
    let cancelled = false;

    (async () => {
      const stripe = await getStripe();
      if (!stripe || cancelled || !topupContainerRef.current) return;
      const elements = stripe.elements({ clientSecret: topupClientSecret, appearance: buildStripeAppearance() });
      elements.create('payment').mount(topupContainerRef.current);
      topupElementsRef.current = { stripe, elements };
    })();

    return () => {
      cancelled = true;
      topupElementsRef.current = null;
    };
  }, [topupClientSecret]);

  const closeTopupDialog = () => {
    setTopupClientSecret(null);
    setTopupPaymentIntentId(null);
    setTopupAmount(null);
    setTopupError('');
  };

  const handleConfirmTopupPayment = async () => {
    if (!topupElementsRef.current) return;
    const { stripe, elements } = topupElementsRef.current;
    setTopupSubmitting(true);
    setTopupError('');
    try {
      const confirmResult = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });
      if (confirmResult.error) {
        setTopupError(confirmResult.error.message || 'Payment failed. Please try again.');
        return;
      }
      // Confirmed inline with no redirect (common card case). Re-verify server-side
      // against Stripe before crediting tokens -- never trust the browser alone.
      const finalizeResp = await api.post(`/billing/stripe/token-topups/finalize/${topupPaymentIntentId}`);
      if (finalizeResp.data?.status === 'complete') {
        toast.success('Tokens added to your account!');
        closeTopupDialog();
        await fetchData(); // same refresh pattern used elsewhere on this page
      } else {
        setTopupError('Payment is still processing. Your tokens will be added automatically once it completes.');
      }
    } catch (err) {
      console.error('Top-up payment confirmation error:', err);
      setTopupError('Something went wrong confirming your payment. Please try again.');
    } finally {
      setTopupSubmitting(false);
    }
  };

  useEffect(() => {
    if (canViewTokenUsage) fetchData();
  }, [canViewTokenUsage, fetchData]);

  const capacityWindows = useMemo(() => (
    [...new Set(userBreakdown
      .map((row) => row.capacity?.window)
      .filter(Boolean))]
      .sort()
  ), [userBreakdown]);

  const filteredUserBreakdown = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    const filtered = userBreakdown.filter((row) => {
      const capacity = row.capacity;
      const matchesSearch = !query
        || getUserDisplayName(row).toLowerCase().includes(query)
        || (row.user_email || '').toLowerCase().includes(query)
        || (row.department || '').toLowerCase().includes(query)
        || (row.job_title || '').toLowerCase().includes(query);
      const matchesCapacity = capacityFilter === 'all'
        || (capacityFilter === 'unlimited' && capacity?.is_unlimited)
        || (capacityFilter === 'exceeded' && capacity?.limit_exceeded)
        || capacity?.window === capacityFilter;
      return matchesSearch && matchesCapacity;
    });

    const getValue = (row) => {
      const capacity = row.capacity;
      switch (sortKey) {
        case 'user':
          return getUserDisplayName(row).toLowerCase();
        case 'request_count':
        case 'total_tokens':
        case 'conversation_tokens':
        case 'knowledge_base_tokens':
        case 'percent_of_total':
          return Number(row[sortKey] || 0);
        case 'capacity':
          return Number(capacity?.percent_used || 0);
        case 'last_active':
          return row.last_active ? new Date(row.last_active).getTime() : 0;
        default:
          return Number(row.total_tokens || 0);
      }
    };

    return [...filtered].sort((a, b) => {
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return (a.user_email || '').localeCompare(b.user_email || '');
    });
  }, [capacityFilter, sortDirection, sortKey, userBreakdown, userSearch]);

  const totalUserPages = Math.max(Math.ceil(filteredUserBreakdown.length / pageSize), 1);
  const currentUserPage = Math.min(page, totalUserPages);
  const pagedUserBreakdown = filteredUserBreakdown.slice(
    (currentUserPage - 1) * pageSize,
    currentUserPage * pageSize
  );

  const updateUserSort = (key) => {
    setPage(1);
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(key === 'user' ? 'asc' : 'desc');
    }
  };

  const resetUserTablePage = () => setPage(1);

  const updateRecentSort = (key) => {
    setRecentRequestsPage(1);
    if (recentSortKey === key) {
      setRecentSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setRecentSortKey(key);
      setRecentSortDirection(key === 'created_at' ? 'desc' : 'asc');
    }
  };

  const resetRecentRequestsPage = () => setRecentRequestsPage(1);

  const recentRequests = (history?.recent_requests || []).slice(0, 50);
  const recentOperationOptions = useMemo(() => {
    const options = new Map();
    recentRequests.forEach((req) => {
      if (req.operation) options.set(req.operation, formatOperationName(req.operation, req.operation_label));
    });
    return Array.from(options, ([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [recentRequests]);
  const recentModelOptions = useMemo(() => (
    Array.from(new Set(recentRequests.map((req) => getEvaModelName(req.model, req.provider, req.model_label)).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
  ), [recentRequests]);
  const filteredRecentRequests = useMemo(() => {
    const query = recentSearch.trim().toLowerCase();
    const rows = recentRequests.filter((req) => {
      const evaModel = getEvaModelName(req.model, req.provider, req.model_label);
      const operationName = formatOperationName(req.operation, req.operation_label);
      const displayName = getUserDisplayName(req);
      const searchable = [
        displayName,
        req.user_email,
        req.department,
        operationName,
        evaModel,
      ].filter(Boolean).join(' ').toLowerCase();

      return (!query || searchable.includes(query))
        && (recentOperationFilter === 'all' || req.operation === recentOperationFilter)
        && (recentModelFilter === 'all' || evaModel === recentModelFilter);
    });

    const getValue = (req) => {
      switch (recentSortKey) {
        case 'user':
          return getUserDisplayName(req).toLowerCase();
        case 'operation':
          return formatOperationName(req.operation, req.operation_label).toLowerCase();
        case 'model':
          return getEvaModelName(req.model, req.provider, req.model_label).toLowerCase();
        case 'total_tokens':
          return Number(req.total_tokens || 0);
        case 'latency_ms':
          return Number(req.latency_ms || 0);
        case 'created_at':
        default:
          return req.created_at ? new Date(req.created_at).getTime() : 0;
      }
    };

    return [...rows].sort((a, b) => {
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue < bValue) return recentSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return recentSortDirection === 'asc' ? 1 : -1;
      return (a.id || '').localeCompare(b.id || '');
    });
  }, [recentModelFilter, recentOperationFilter, recentRequests, recentSearch, recentSortDirection, recentSortKey]);
  const totalRecentRequestPages = Math.max(Math.ceil(filteredRecentRequests.length / RECENT_REQUESTS_PAGE_SIZE), 1);
  const currentRecentRequestPage = Math.min(recentRequestsPage, totalRecentRequestPages);
  const pagedRecentRequests = filteredRecentRequests.slice(
    (currentRecentRequestPage - 1) * RECENT_REQUESTS_PAGE_SIZE,
    currentRecentRequestPage * RECENT_REQUESTS_PAGE_SIZE
  );

  if (!canViewTokenUsage) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <Card className="bg-warning/5 border-warning/30">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertTriangle className="h-8 w-8 text-warning" />
            <div>
              <h3 className="font-semibold">Access Denied</h3>
              <p className="text-muted-foreground">Only analysts and administrators can view AI usage.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="7xl"
        kpis={4}
        tabs
        table
        sections={1}
        sectionClassName="h-80"
        data-testid="token-usage-loading"
      />
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <Alert variant="destructive" data-testid="token-usage-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const { plan, period, tokens, status, breakdown, token_buckets: tokenBuckets } = usage || {};
  const isUnlimited = status?.is_unlimited || tokens?.limit === null;
  const usagePercent = isUnlimited ? 0 : (tokens?.percent_used || 0);
  const isWarning = !isUnlimited && usagePercent >= 80;
  const isExceeded = !isUnlimited && status?.limit_exceeded;
  const monthlyTokenUsageCard = !isUnlimited && (
    <LimitMeter
      title="Monthly AI Credits"
      used={tokens?.used || 0}
      limit={tokens?.limit}
      icon={Zap}
      formatter={formatTokens}
      displayType="metered"
      percentUsed={usagePercent}
      caption={`${formatTokens(tokens?.used)} / ${formatTokens(tokens?.limit)} credits used this billing period`}
      explanation={period?.end ? `Resets ${new Date(period.end).toLocaleDateString()}` : undefined}
      showManagePlanCta
      managePlanCtaLabel={isExceeded ? 'Upgrade Plan' : 'Manage Plan'}
      testId="usage-progress"
    />
  );

  const dailyData = buildTrendData(history, trendWindow);

  const operationData = breakdown?.by_operation?.map(op => ({
    name: formatOperationName(op.operation, op.operation_label),
    value: op.tokens,
  })) || [];

  const modelData = Object.values((breakdown?.by_model || []).reduce((acc, item) => {
    const name = getEvaModelName(item.model, item.provider, item.model_label);
    acc[name] = acc[name] || { name, tokens: 0, rawModels: [] };
    acc[name].tokens += Number(item.tokens || 0);
    if (item.model && !acc[name].rawModels.includes(item.model)) acc[name].rawModels.push(item.model);
    return acc;
  }, {}));
  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8 animate-fade-in" data-testid="token-usage-page">
      {ConfirmEl}

      <Dialog open={Boolean(topupClientSecret)} onOpenChange={(open) => { if (!open) closeTopupDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Buy token top-up</DialogTitle>
            <DialogDescription>
              {topupAmount
                ? `You'll be charged $${Number(topupAmount.amount).toFixed(2)} ${(topupAmount.currency || 'usd').toUpperCase()} for ${formatTokens(topupAmount.tokens)} tokens, added to your account immediately upon payment.`
                : 'Complete your payment to add tokens to your account.'}
            </DialogDescription>
          </DialogHeader>
          <div ref={topupContainerRef} data-testid="stripe-topup-payment-element" />
          {topupError && <p className="text-sm text-destructive">{topupError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={closeTopupDialog} disabled={topupSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleConfirmTopupPayment} disabled={topupSubmitting}>
              {topupSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Banner */}
      <PageBanner pageKey="settings-usage" />

      <PageHeader
        title="AI Usage"
        icon={Zap}
        description="How much your team uses Eva — every question, summary, and document analyzed counts toward your monthly credits"
        actions={(
          <>
          <Badge variant={isUnlimited || plan?.type?.includes('enterprise') ? 'default' : 'secondary'}>
            {plan?.display_name || 'Trial'} Plan
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          </>
        )}
      />

      {/* Unlimited Plan Banner */}
      {isUnlimited && (
        <Alert className="border-success/50 bg-success/10" data-testid="unlimited-plan-alert">
          <Zap className="h-4 w-4 text-success" />
          <AlertTitle className="text-success">Unlimited Plan</AlertTitle>
          <AlertDescription>
            Your {plan?.display_name || 'Enterprise'} plan has unlimited usage. No usage restrictions apply.
          </AlertDescription>
        </Alert>
      )}

      {/* Warning/Limit Exceeded Alert */}
      {isExceeded && (
        <Alert variant="destructive" data-testid="limit-exceeded-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Credit Limit Exceeded</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>Your monthly credit limit has been exceeded. Upgrade your plan to continue using AI features.</span>
            <Button variant="outline" size="sm" asChild>
              <a href="/settings/billing">
                <ExternalLink className="w-4 h-4 mr-2" />
                Upgrade Plan
              </a>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isWarning && !isExceeded && (
        <Alert variant="warning" className="border-warning/50 bg-warning/10" data-testid="limit-warning-alert">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <AlertTitle className="text-warning">Approaching Credit Limit</AlertTitle>
          <AlertDescription>
            You have used {usagePercent.toFixed(1)}% of your monthly credit limit. Consider upgrading to avoid service interruption.
          </AlertDescription>
        </Alert>
      )}

      {/* Pending Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="usage-alerts">
          {alerts.map((alert) => (
            <Alert 
              key={alert.id} 
              className="border-warning/50 bg-warning/10"
            >
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertTitle className="text-warning flex items-center justify-between">
                <span>Credit Alert: {alert.threshold_pct}% Threshold Reached</span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => dismissAlert(alert.id)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Dismiss
                </Button>
              </AlertTitle>
            <AlertDescription>
                You have used {formatPercent(alert.current_usage_pct)} of your credit limit ({formatTokens(alert.tokens_used)} of {formatTokens(alert.tokens_limit)} credits).
                Created {new Date(alert.created_at).toLocaleString()}.
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Used"
          value={formatTokens(tokens?.used)}
          subtitle={isUnlimited ? 'Unlimited plan' : `of ${formatTokens(tokens?.limit)} credits`}
          icon={Zap}
          color="primary"
        />
        <StatCard
          title="Remaining"
          value={isUnlimited ? '∞' : formatTokens(tokens?.remaining)}
          subtitle={isUnlimited ? 'Unlimited' : `${formatPercent(Math.max(100 - usagePercent, 0))} of limit`}
          icon={TrendingUp}
          color={isUnlimited ? 'success' : isWarning ? 'warning' : 'info'}
        />
        <StatCard
          title="Usage"
          value={isUnlimited ? 'Unlimited' : formatPercent(usagePercent)}
          subtitle={isUnlimited ? 'No usage cap' : 'of monthly AI credits'}
          icon={BarChart3}
          color={isExceeded ? 'destructive' : isWarning ? 'warning' : 'success'}
        />
        <StatCard
          title="Period Ends"
          value={period?.end ? new Date(period.end).toLocaleDateString() : 'N/A'}
          subtitle="Monthly reset"
          icon={Calendar}
          color="secondary"
        />
      </div>

      {monthlyTokenUsageCard}

      {!isUnlimited && tokenBuckets && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <LimitMeter
            title={EVA_PEOPLE_TOKEN_LABEL}
            used={tokenBuckets.conversation?.used || 0}
            limit={tokenBuckets.conversation?.limit}
            icon={Users}
            formatter={formatTokens}
            displayType="metered"
            percentUsed={tokenBuckets.conversation?.percent_used || 0}
            caption="Employee-facing usage in Eva People"
            showManagePlanCta
            testId="conversation-token-bucket"
          />
          <LimitMeter
            title={EVA_INSIGHTS_TOKEN_LABEL}
            used={tokenBuckets.knowledge_base?.used || 0}
            limit={tokenBuckets.knowledge_base?.limit}
            icon={Cpu}
            formatter={formatTokens}
            displayType="metered"
            percentUsed={tokenBuckets.knowledge_base?.percent_used || 0}
            caption="Content team usage in Eva Insights"
            showManagePlanCta
            testId="kb-token-bucket"
          />
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ShoppingCart className="h-4 w-4" />
                    Extra Credit Balance
                  </CardTitle>
                  <CardDescription>Purchased overflow credits will apply to either bucket.</CardDescription>
                </div>
                <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">
                  Coming Soon
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-3xl font-bold">{formatTokens(tokenBuckets.topup?.remaining || 0)}</p>
                <p className="text-sm text-muted-foreground">remaining purchased credits</p>
              </div>
              <div className="grid gap-2">
                {topupOptions.map((pack) => (
                  <Button
                    key={pack.llm_tokens}
                    variant="outline"
                    className="justify-between opacity-60"
                    onClick={() => buyTopup(pack)}
                    disabled
	                  >
	                    <span>{formatTokens(pack.tokens)} credits</span>
	                    <span aria-label="Price unavailable">&mdash;</span>
	                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <AnalyticsTabBar />

        <TabsContent value="details" className="space-y-6">
          {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4" />
                Usage by User
              </CardTitle>
              <CardDescription>All users in the current 30-day window</CardDescription>
            </CardHeader>
            <CardContent>
              {userBreakdown.length > 0 ? (
                <div className="space-y-4">
                  <DataTableToolbar
                    searchValue={userSearch}
                    onSearchChange={(value) => {
                      setUserSearch(value);
                      resetUserTablePage();
                    }}
                    searchPlaceholder="Search users..."
                    searchTestId="token-usage-user-search"
                    className="border-0 bg-transparent p-0"
                  >
                    <Select
                      value={capacityFilter}
                      onValueChange={(value) => {
                        setCapacityFilter(value);
                        resetUserTablePage();
                      }}
                    >
                      <SelectTrigger className="w-full sm:w-44" data-testid="token-usage-capacity-filter">
                        <SelectValue placeholder="Rate limit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All rate limits</SelectItem>
                        {capacityWindows.map((window) => (
                          <SelectItem key={window} value={window} className="capitalize">
                            {window}
                          </SelectItem>
                        ))}
                        <SelectItem value="unlimited">Unlimited</SelectItem>
                        <SelectItem value="exceeded">Over limit</SelectItem>
                      </SelectContent>
                    </Select>
                  </DataTableToolbar>

                  {filteredUserBreakdown.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[1260px] data-table">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="pb-3 pr-6 text-left text-muted-foreground">
                                <DataTableSortHeader label="User" sortKey="user" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateUserSort} />
                              </th>
                              <th className="pb-3 px-4 text-center text-muted-foreground">
                                <DataTableSortHeader label="Requests" sortKey="request_count" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateUserSort} align="center" />
                              </th>
                              <th className="pb-3 px-4 text-center text-muted-foreground">
                                <DataTableSortHeader label="Eva People" sortKey="conversation_tokens" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateUserSort} align="center" />
                              </th>
                              <th className="pb-3 px-4 text-center text-muted-foreground">
                                <DataTableSortHeader label="Eva Insights" sortKey="knowledge_base_tokens" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateUserSort} align="center" />
                              </th>
                              <th className="pb-3 px-4 text-center text-muted-foreground">
                                <DataTableSortHeader label="Total" sortKey="total_tokens" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateUserSort} align="center" />
                              </th>
                              <th className="pb-3 px-4 text-left text-muted-foreground">
                                <DataTableSortHeader label="Rate limit" sortKey="capacity" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateUserSort} />
                              </th>
                              <th className="pb-3 px-4 text-center text-muted-foreground">
                                <DataTableSortHeader label="Share" sortKey="percent_of_total" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateUserSort} align="center" />
                              </th>
                              <th className="pb-3 px-4 text-center text-muted-foreground">
                                <DataTableSortHeader label="Last Active" sortKey="last_active" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateUserSort} align="center" />
                              </th>
                              {isAdmin && (
                                <th className="pb-3 pl-4 text-right text-muted-foreground">Actions</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {pagedUserBreakdown.map((row) => {
                              const capacity = row.capacity;
                              const displayName = getUserDisplayName(row);
                              const secondaryDetails = [
                                displayName === row.user_email ? null : row.user_email,
                                row.department,
                              ].filter(Boolean).join(' · ');
                              const userIdentity = {
                                name: displayName,
                                email: row.user_email,
                                avatar_url: row.avatar_url,
                              };
                              return (
                                <tr key={row.user_email} className="border-b border-border/50 hover:bg-secondary/30">
                                  <td className="py-3 pr-6">
                                    <div className="flex items-center gap-3">
                                      <UserAvatar user={userIdentity} size="sm" />
                                      <div className="min-w-0">
                                        <UserNameWithRole name={displayName} isSuperadmin={row.is_superadmin} />
                                        {secondaryDetails && (
                                          <p className="truncate text-xs text-muted-foreground">{secondaryDetails}</p>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 text-center">{formatCellValue(row.request_count)}</td>
                                  <td className="py-3 px-4 text-center">{row.conversation_tokens === null || row.conversation_tokens === undefined ? formatCellValue(row.conversation_tokens) : formatTokens(row.conversation_tokens)}</td>
                                  <td className="py-3 px-4 text-center">{row.knowledge_base_tokens === null || row.knowledge_base_tokens === undefined ? formatCellValue(row.knowledge_base_tokens) : formatTokens(row.knowledge_base_tokens)}</td>
                                  <td className="py-3 px-4 text-center">{row.total_tokens === null || row.total_tokens === undefined ? formatCellValue(row.total_tokens) : formatTokens(row.total_tokens)}</td>
                                  <td className="py-3 px-4 min-w-[190px]">
                                    {capacity?.is_unlimited ? (
                                      <Badge variant="default">Unlimited</Badge>
                                    ) : capacity ? (
                                      <div className="space-y-1">
                                        <div className="flex items-center justify-between gap-3 text-xs">
                                          <span className="capitalize text-muted-foreground">{capacity.window}</span>
                                          <span className={capacity.limit_exceeded ? 'font-semibold text-destructive' : 'font-medium'}>
                                            {formatPercent(capacity.percent_used)}
                                          </span>
                                        </div>
                                        <Progress
                                          value={Math.min(Number(capacity.percent_used || 0), 100)}
                                          className={`h-1.5 ${getCapacityProgressClass(capacity)}`}
                                        />
                                        <p className="text-[11px] leading-4 text-muted-foreground">
                                          Resets in {capacity.resets_in_label || 'soon'}
                                        </p>
                                      </div>
                                    ) : (
                                      <span className="text-muted-foreground">{formatCellValue(null)}</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4 text-center">{row.percent_of_total === null || row.percent_of_total === undefined ? formatCellValue(row.percent_of_total) : formatPercent(row.percent_of_total)}</td>
                                  <td className="py-3 px-4 text-center">{row.last_active ? new Date(row.last_active).toLocaleDateString() : formatCellValue(row.last_active)}</td>
                                  {isAdmin && (
                                    <td className="py-3 pl-4 text-right">
                                      {row.is_superadmin || capacity?.is_unlimited ? (
                                        <span className="text-xs text-muted-foreground">—</span>
                                      ) : (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-8"
                                          disabled={resettingEmail === row.user_email}
                                          onClick={() => handleResetUserUsage(row)}
                                          data-testid="reset-user-usage"
                                          title="Reset this user's personal AI usage limit"
                                        >
                                          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${resettingEmail === row.user_email ? 'animate-spin' : ''}`} />
                                          Reset limit
                                        </Button>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <DataTablePagination
                        page={currentUserPage}
                        totalPages={totalUserPages}
                        totalItems={filteredUserBreakdown.length}
                        pageSize={pageSize}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        onPageChange={setPage}
                        onPageSizeChange={(value) => {
                          setPageSize(value);
                          setPage(1);
                        }}
                        itemLabel="users"
                        pageSizeTestId="token-usage-page-size"
                      />
                    </>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">No users match the current filters</div>
                  )}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">No user usage data yet</div>
              )}
            </CardContent>
          </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Requests</CardTitle>
              <CardDescription>Latest 50 API calls</CardDescription>
            </CardHeader>
            <CardContent>
              {recentRequests.length > 0 ? (
                <div className="space-y-4">
                  <DataTableToolbar
                    searchValue={recentSearch}
                    onSearchChange={(value) => {
                      setRecentSearch(value);
                      resetRecentRequestsPage();
                    }}
                    searchPlaceholder="Search requests..."
                    searchTestId="token-usage-recent-search"
                    className="border-0 bg-transparent p-0"
                  >
                    <Select
                      value={recentOperationFilter}
                      onValueChange={(value) => {
                        setRecentOperationFilter(value);
                        resetRecentRequestsPage();
                      }}
                    >
                      <SelectTrigger className="w-full sm:w-52" data-testid="token-usage-recent-operation-filter">
                        <SelectValue placeholder="Operation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All operations</SelectItem>
                        {recentOperationOptions.map((operation) => (
                          <SelectItem key={operation.value} value={operation.value}>
                            {operation.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={recentModelFilter}
                      onValueChange={(value) => {
                        setRecentModelFilter(value);
                        resetRecentRequestsPage();
                      }}
                    >
                      <SelectTrigger className="w-full sm:w-44" data-testid="token-usage-recent-model-filter">
                        <SelectValue placeholder="Eva Model" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All models</SelectItem>
                        {recentModelOptions.map((model) => (
                          <SelectItem key={model} value={model}>
                            {model}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DataTableToolbar>

                  {filteredRecentRequests.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[980px] data-table">
                          <thead>
                            <tr className="border-b">
                              <th className="pb-2 pr-4 text-left text-muted-foreground">
                                <DataTableSortHeader label="Time" sortKey="created_at" activeSortKey={recentSortKey} sortDirection={recentSortDirection} onSort={updateRecentSort} />
                              </th>
                              <th className="pb-2 px-4 text-left text-muted-foreground">
                                <DataTableSortHeader label="User" sortKey="user" activeSortKey={recentSortKey} sortDirection={recentSortDirection} onSort={updateRecentSort} />
                              </th>
                              <th className="pb-2 px-4 text-left text-muted-foreground">
                                <DataTableSortHeader label="Operation" sortKey="operation" activeSortKey={recentSortKey} sortDirection={recentSortDirection} onSort={updateRecentSort} />
                              </th>
                              <th className="pb-2 px-4 text-left text-muted-foreground">
                                <DataTableSortHeader label="Eva Model" sortKey="model" activeSortKey={recentSortKey} sortDirection={recentSortDirection} onSort={updateRecentSort} />
                              </th>
                              <th className="pb-2 px-4 text-center text-muted-foreground">
                                <DataTableSortHeader label="Usage" sortKey="total_tokens" activeSortKey={recentSortKey} sortDirection={recentSortDirection} onSort={updateRecentSort} align="center" />
                              </th>
                              <th className="pb-2 pl-4 text-center text-muted-foreground">
                                <DataTableSortHeader label="Latency" sortKey="latency_ms" activeSortKey={recentSortKey} sortDirection={recentSortDirection} onSort={updateRecentSort} align="center" />
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedRecentRequests.map((req, idx) => {
                              const displayName = getUserDisplayName(req);
                              const secondaryDetails = [
                                displayName === req.user_email ? null : req.user_email,
                                req.department,
                              ].filter(Boolean).join(' · ');
                              const userIdentity = {
                                name: displayName,
                                email: req.user_email,
                                avatar_url: req.avatar_url,
                              };
                              const operationLabel = formatOperationName(req.operation, req.operation_label);
                              const OperationIcon = OPERATION_ICON_MAP[req.operation];
                              const modelLabel = getEvaModelName(req.model, req.provider, req.model_label);
                              const ModelIcon = MODEL_ICON_MAP[modelLabel];

                              return (
                                <tr key={req.id || idx} className="border-b border-border/50 hover:bg-secondary/30">
                                  <td className="py-2 pr-4">{req.created_at ? new Date(req.created_at).toLocaleString() : formatCellValue(req.created_at)}</td>
                                  <td className="py-2 px-4">
                                    <div className="flex items-center gap-3">
                                      <UserAvatar user={userIdentity} size="sm" />
                                      <div className="min-w-0">
                                        <UserNameWithRole name={displayName} isSuperadmin={req.is_superadmin} />
                                        {secondaryDetails && (
                                          <p className="truncate text-xs text-muted-foreground">{secondaryDetails}</p>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-2 px-4">
                                    <IconLabel icon={OperationIcon}>{operationLabel}</IconLabel>
                                  </td>
                                  <td className="py-2 px-4">
                                    <Badge variant="outline" className="gap-1.5">
                                      <IconLabel icon={ModelIcon}>{modelLabel}</IconLabel>
                                    </Badge>
                                  </td>
                                  <td className="py-2 px-4 text-center">{req.total_tokens === null || req.total_tokens === undefined ? formatCellValue(req.total_tokens) : formatTokens(req.total_tokens)}</td>
                                  <td className="py-2 pl-4 text-center">{formatLatencySeconds(req.latency_ms)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <DataTablePagination
                        page={currentRecentRequestPage}
                        totalPages={totalRecentRequestPages}
                        totalItems={filteredRecentRequests.length}
                        pageSize={RECENT_REQUESTS_PAGE_SIZE}
                        onPageChange={setRecentRequestsPage}
                        itemLabel="requests"
                        pageSizeTestId="token-usage-recent-page-size"
                      />
                    </>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">No requests match the current filters</div>
                  )}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">No recent requests yet</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  AI Usage Trend ({getTrendRangeLabel(trendWindow)})
                </CardTitle>
                <TrendRangeTabs value={trendWindow} onChange={setTrendWindow} className="w-full sm:w-auto sm:min-w-[320px]" />
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyData}>
                      <CartesianGrid {...commonCartesianGridProps} />
                      <XAxis
                        dataKey="date"
                        {...commonXAxisProps}
                        interval="preserveStartEnd"
                      />
                      <YAxis {...commonYAxisProps} tickFormatter={formatTokens} />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        formatter={(value, _name) => [formatTokens(value), 'Usage']}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.fullLabel || label}
                      />
                      <Legend {...commonLegendProps} />
                      <Line
                        type="monotone"
                        dataKey="tokens"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={dailyData.length <= 2 ? { r: 3 } : false}
                        activeDot={{ r: 4 }}
                        name="Usage"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Cpu className="w-4 h-4" />
                  Usage by Operation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {operationData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={operationData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {operationData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={chartTooltipStyle} formatter={(value) => formatTokens(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                      No usage data yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {modelData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Usage by Eva Model
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={modelData} layout="vertical">
                      <CartesianGrid {...commonCartesianGridProps} />
                      <XAxis type="number" {...commonXAxisProps} tickFormatter={formatTokens} />
                      <YAxis dataKey="name" type="category" width={120} {...commonYAxisProps} />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        formatter={(value, _name) => [formatTokens(value), 'Usage']}
                      />
                      <Legend {...commonLegendProps} />
                      <Bar dataKey="tokens" fill="#3b82f6" name="Usage" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="world">
          <RegionalMapView
            title="Usage Map"
            description="AI usage by employee location"
            regions={((mapHistory || history)?.regional_usage || []).map((region) => ({
              region: region.region,
              value: Number(region.tokens || 0),
              secondaryValue: Number(region.requests || 0),
            }))}
            countries={((mapHistory || history)?.country_usage || []).map((country) => ({
              country: country.country,
              region: country.region,
              value: Number(country.tokens || 0),
              secondaryValue: Number(country.requests || 0),
            }))}
            metricLabel="Usage"
            secondaryLabel="Requests"
            colorScale="blue"
            emptyMessage="The usage map will appear once employees with locations start using Eva."
            timeRangeValue={mapWindow}
            onTimeRangeChange={setMapWindow}
            loading={mapLoading}
          />
        </TabsContent>
      </Tabs>

      {/* Plan Info Footer */}
      <Card className="bg-secondary/30">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="text-sm">
            <span className="text-muted-foreground">Current Plan: </span>
            <span className="font-medium">{plan?.display_name || 'Trial'}</span>
            <span className="text-muted-foreground"> • </span>
            <span className="text-muted-foreground">
              {formatTokens(tokens?.limit)} usage/month
            </span>
          </div>
          <Button variant="outline" size="sm" asChild>
            <a href="/settings/billing">
              Manage Plan
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
